/**
 * Links from a mission to real Doc records, including the one-time migration
 * that attaches those docs.
 */

export function useAttachedDocs(localSprint, setLocalSprint, recordAction) {
  /**
   * Docs attached to a mission (Mission Docs Become Real Docs):
   * `task.attachedDocs` = [{ docId }] — references to real Doc records, in
   * display order. Attach/detach only moves the LINK; doc records themselves
   * are never created or deleted here.
   */
  const handleAttachedDocsChange = (taskId, attachedDocs) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldAttached = task?.attachedDocs || [];

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t =>
          t.id === taskId ? { ...t, attachedDocs, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CHANGE_ATTACHED_DOCS',
        description: `${taskName}: Attached docs changed`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, attachedDocs: oldAttached } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, attachedDocs } : t),
            };
          });
        },
      });
    }
  };

  /**
   * Records the one-time migration of a mission's embedded docs into real
   * Doc records (see utils/missionDocMigration.js). Merges the new entries
   * into `attachedDocs` (deduped) and stamps `docsMigratedAt` IN THE SAME
   * update, so a re-render can't trigger a second migration. Silent: an
   * automatic data move must not enter undo history (undoing it would
   * orphan the created docs). Legacy fields (docUser/docAdmin/notes/
   * internalNotes) are never touched — they stay as frozen backups.
   */
  const handleMissionDocsMigrated = (taskId, entries) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t => {
          if (t.id !== taskId) return t;
          const existing = Array.isArray(t.attachedDocs) ? t.attachedDocs : [];
          const known = new Set(existing.map(e => e.docId));
          const added = (entries || []).filter(e => e?.docId && !known.has(e.docId));
          return {
            ...t,
            attachedDocs: [...existing, ...added],
            docsMigratedAt: new Date().toISOString(),
          };
        }),
      };
    });
  };

  return {
    handleAttachedDocsChange,
    handleMissionDocsMigrated,
  };
}
